import React, { ReactElement, useCallback, useEffect } from 'react';
import { Loading } from '@dexma/ui-components';
import { isSome, isNone } from 'fp-ts/Option';

import { CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import { useSettings, dispatchSettings, MaybeSettingsError, MaybeSettings } from 'adapter/in/dashboards/context/Settings/data';
import { dispatchSettingsView } from 'adapter/in/dashboards/context/Settings/view';

export const useReadSettings = (): {
    success: boolean;
    isLoading: boolean;
    settings: MaybeSettings;
    error: MaybeSettingsError;
    read: CriteriaCallback;
} => {
    const { isLoading, settings, error } = useSettings();
    const { read } = dispatchSettings();
    const { setSettings } = dispatchSettingsView();

    useEffect(() => {
        if (isSome(settings)) setSettings(settings.value);
    }, [settings]);

    const handleRead = useCallback(() => read(), []);

    useEffect(() => {
        handleRead();
    }, []);

    return {
        success: !!isSome(settings),
        isLoading,
        settings,
        error,
        read: handleRead,
    };
};

type SettingsTypes = {
    children: ReactElement;
};

export const SettingsContainer = (props: SettingsTypes): ReactElement => {
    const { children } = props;
    const { isLoading: isLoadingSettings, settings, error: hasErrorSettings } = useReadSettings();

    return isSome(settings) && !isLoadingSettings && isNone(hasErrorSettings) ? (
        children
    ) : (
        <Loading isLoading size={50} className='loading' />
    );
};
